import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Toggle from 'react-toggled';
import {
  CreditClaimDeletedFilter,
  CreditClaimDeletedFilterValues,
  CreditClaimStatus,
  CreditClaimStatusValues,
  creditClaimStatusOptions,
  StatusCheckbox,
} from '../../constants/ApplicationStatus';
import { ApplicationTypeFilter } from '../../constants/ApplicationTypeFilter';
import { fetchClaims, setClaimDeletionFilter, setClaimStatusFilters } from '../../ducks/claims';
import { showModal } from '../../ducks/modal';
import DefaultPage from '../../layouts/DefaultPage';
import { PageSectionTitle } from '../../layouts/PageStructure';
import {
  claimLoadingSelector,
  claimsDeletedFilterSelector,
  claimsStatusFiltersSelector,
  claimsTotalSelector,
} from '../../selectors/claims';
import { isAdmin as isAdminSelector, isCreditProcessor } from '../../selectors/user';
import styled from '../../theme';
import primaryTheme, { Color } from '../../theme/primaryTheme';
import ApplicationTable from '../ApplicationTable';
import { DesktopPromptModal, desktopPromptModalId } from '../DesktopPromptModal';
import { Dropdown, DropdownButton, DropdownPanel } from '../Dropdown';
import CheckBox from '../Field/Controls/CheckBox';
import { Grid } from '../Grid';
import NewItemButton, { ItemType } from '../NewItemButton';
import Text, { TextStyle } from '../Text';

export enum ApplicationSort {
  Name = 'NAME',
  ApplicationType = 'APPLICATION_TYPE',
  ProcessingRate = 'PROCESSING_RATE',
  PaymentType = 'PAYMENT_TYPE',
  DateReceived = 'dateReceived',
  DueDate = 'DUE_DATE',
}

const ApplicationIndex: React.FunctionComponent = () => {
  const { innerWidth } = window;
  const dispatch = useDispatch();
  const isProcessor = useSelector(isCreditProcessor);
  const isAdmin = useSelector(isAdminSelector);
  const total = useSelector(claimsTotalSelector);
  const isLoading = useSelector(claimLoadingSelector);
  const statusFilters = useSelector(claimsStatusFiltersSelector);
  const deletedFilter = useSelector(claimsDeletedFilterSelector);

  React.useEffect(() => {
    // Display modal at widths below the 'medium' breakpoint
    if (innerWidth < parseInt(primaryTheme.breakpoints.medium.slice(0, -2), 10)) {
      dispatch(showModal(desktopPromptModalId));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [typeFilter, setTypeFilter] = useState(ApplicationTypeFilter.All);
  const savedStatusFilters = React.useRef<CreditClaimStatus[] | null>(null);

  const setTypeFilterToAll = () => {
    setTypeFilter(ApplicationTypeFilter.All);
    dispatch(fetchClaims({ applicationTypeFilter: ApplicationTypeFilter.All, page: 1, withDeleted: deletedFilter }));
  };

  const setTypeFilterToDirectCredit = () => {
    setTypeFilter(ApplicationTypeFilter.DirectCredit);
    dispatch(fetchClaims({ applicationTypeFilter: ApplicationTypeFilter.DirectCredit, page: 1, withDeleted: deletedFilter }));
  };

  const setTypeFilterToInternationalCredit = () => {
    setTypeFilter(ApplicationTypeFilter.InternationalCredit);
    dispatch(fetchClaims({ applicationTypeFilter: ApplicationTypeFilter.InternationalCredit, page: 1, withDeleted: deletedFilter }));
  };

  const setTypeFilterToPRAAward = () => {
    setTypeFilter(ApplicationTypeFilter.PRA);
    dispatch(fetchClaims({ applicationTypeFilter: ApplicationTypeFilter.PRA, page: 1, withDeleted: deletedFilter }));
  };

  const handleStatusFilter = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.currentTarget;
    const selectedCreditClaimStatus = name as CreditClaimStatus;
    const index = statusFilters.indexOf(selectedCreditClaimStatus);
    const newStatusFilters = [...statusFilters];

    if (index > -1) {
      newStatusFilters.splice(index, 1);
    } else {
      newStatusFilters.push(selectedCreditClaimStatus);
    }

    dispatch(setClaimStatusFilters(newStatusFilters));
    dispatch(fetchClaims({ applicationTypeFilter: typeFilter, status: newStatusFilters, page: 1, withDeleted: deletedFilter }));
  };

  const handleDeletionFilter = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.currentTarget;
    const newDeletionFilter = name as CreditClaimDeletedFilter;

    let newStatusFilters: CreditClaimStatus[];
    if (newDeletionFilter === CreditClaimDeletedFilter.ONLY_DELETED) {
      savedStatusFilters.current = statusFilters;
      newStatusFilters = CreditClaimStatusValues;
    } else if (deletedFilter === CreditClaimDeletedFilter.ONLY_DELETED) {
      newStatusFilters = savedStatusFilters.current ?? statusFilters;
      savedStatusFilters.current = null;
    } else {
      newStatusFilters = statusFilters;
    }

    dispatch(setClaimDeletionFilter(newDeletionFilter));
    dispatch(setClaimStatusFilters(newStatusFilters));
    dispatch(fetchClaims({ applicationTypeFilter: typeFilter, status: newStatusFilters, page: 1, withDeleted: newDeletionFilter }));
  };

  return (
    <DefaultPage
      title={isProcessor ? 'Application Queue' : 'My Applications'}
      action={<NewItemButton itemType={ItemType.Application} />}
      tabs={[
        {
          id: 'all',
          label: 'All',
          selected: typeFilter === ApplicationTypeFilter.All,
          onClick: setTypeFilterToAll,
        },
        {
          id: 'direct',
          label: 'Direct Credit',
          selected: typeFilter === ApplicationTypeFilter.DirectCredit,
          onClick: setTypeFilterToDirectCredit,
        },
        {
          id: 'international',
          label: 'Credit Conversion',
          selected: typeFilter === ApplicationTypeFilter.InternationalCredit,
          onClick: setTypeFilterToInternationalCredit,
        },
        {
          id: 'pra',
          label: 'PRA',
          selected: typeFilter === ApplicationTypeFilter.PRA,
          onClick: setTypeFilterToPRAAward,
        },
      ]}
    >
      <PageSectionTitle>
        <DesktopPromptModal />
        <Text textStyle={TextStyle.SectionTitle} bold={true} tag="h3">
          All Applications {isLoading ? '(...)' : total !== undefined && `(${total})`}
        </Text>
        <div>
          <Grid gridTemplateColumns="auto auto">
            {(isProcessor || isAdmin) && (
              <Toggle>
                {({ on, toggle, setOff, getTogglerProps }) => (
                  <Dropdown>
                    <DropdownButton
                      isOpen={on}
                      onClick={toggle}
                      removePadding={true}
                      colorKey={Color.Label}
                      data-test-id="openDeletedFilter"
                      {...getTogglerProps()}
                    >
                      Filter By Deletion
                    </DropdownButton>
                    {on && (
                      <DropdownPanel arrow={true} onClickOutside={setOff} isOpen={on} width={250}>
                        <ApplicationStatusFilters data-test-id="deletedFilterMenu">
                          {CreditClaimDeletedFilterValues.map((deletionFilter) => (
                            <CheckBox
                              key={deletionFilter}
                              name={deletionFilter}
                              title={deletionFilter}
                              checked={deletedFilter === deletionFilter}
                              disabled={isLoading}
                              textProps={{
                                bold: false,
                                textStyle: TextStyle.Small,
                              }}
                              onChange={handleDeletionFilter}
                            />
                          ))}
                        </ApplicationStatusFilters>
                      </DropdownPanel>
                    )}
                  </Dropdown>
                )}
              </Toggle>
            )}
            {(isProcessor || isAdmin) && (
              <Toggle>
                {({ on, toggle, setOff, getTogglerProps }) => (
                  <Dropdown>
                    <DropdownButton
                      isOpen={on}
                      onClick={toggle}
                      removePadding={true}
                      colorKey={Color.Label}
                      data-test-id="openStatusFilter"
                      {...getTogglerProps()}
                    >
                      Filter By Status
                    </DropdownButton>
                    {on && (
                      <DropdownPanel arrow={true} onClickOutside={setOff} isOpen={on} width={250}>
                        <ApplicationStatusFilters data-test-id="statusFilterMenu">
                          {creditClaimStatusOptions.map((status: StatusCheckbox) => (
                            <CheckBox
                              key={status.value}
                              name={status.value}
                              title={status.label}
                              checked={statusFilters.includes(status.value)}
                              disabled={isLoading}
                              textProps={{
                                bold: false,
                                textStyle: TextStyle.Small,
                              }}
                              onChange={handleStatusFilter}
                            />
                          ))}
                        </ApplicationStatusFilters>
                      </DropdownPanel>
                    )}
                  </Dropdown>
                )}
              </Toggle>
            )}
          </Grid>
        </div>
      </PageSectionTitle>
      <ApplicationTable applicationTypeFilter={typeFilter} statusFilters={statusFilters} deletedFilter={deletedFilter} />
    </DefaultPage>
  );
};

const ApplicationStatusFilters = styled.div`
  padding: 2rem;

  > div + div {
    margin-top: 0.25em;
  }
`;

export default ApplicationIndex;
